import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';

interface HeroSectionProps {
  scrolled: boolean;
}

export default function HeroSection({ scrolled }: HeroSectionProps) {
  const { t } = useTranslation('common');

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
      setLanguageMenuOpen(false);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
  };

  const languages = [
    { code: 'en', name: 'English', flag: 'EN' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <section id="overview" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Circuit Board Background */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.readdy.ai/image/c07ed6529feea951012003251bff6ba4/78a627228f114a68e878a20237e5938e.jpeg)' }}></div>
      <div className="absolute inset-0 bg-black/70"></div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl shadow-lg' : 'bg-white/10 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/c07ed6529feea951012003251bff6ba4/cc46e5df510751817c67bd8191e21399.png" 
                alt="Company Logo" 
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-10">
              <button 
                onClick={() => scrollToSection('overview')}
                className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-[#4d9c71]' : 'text-white hover:text-[#4d9c71]'
                }`}
              >
                {t('nav.overview')}
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-[#4d9c71]' : 'text-white hover:text-[#4d9c71]'
                }`}
              >
                {t('nav.services')}
              </button>
              <button 
                onClick={() => scrollToSection('industries')}
                className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-[#4d9c71]' : 'text-white hover:text-[#4d9c71]'
                }`}
              >
                {t('nav.industries')}
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-[#4d9c71]' : 'text-white hover:text-[#4d9c71]'
                }`}
              >
                {t('nav.about')}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-[#4d9c71]' : 'text-white hover:text-[#4d9c71]'
                }`}
              >
                {t('nav.contact')}
              </button>
            </div>

            {/* Language Selector & CTA */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <span className="text-xl">{currentLanguage.flag}</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform ${languageMenuOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {languageMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[160px]">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap ${
                          i18n.language === lang.code ? 'bg-[#4d9c71]/10 text-[#4d9c71]' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button 
                onClick={scrollToContact}
                className="px-6 py-3 bg-[#4d9c71] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#4d9c71]/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('nav.getStarted')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection('overview')}
                className="block w-full text-left text-gray-700 hover:text-[#4d9c71] font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav.overview')}
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="block w-full text-left text-gray-700 hover:text-[#4d9c71] font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav.services')}
              </button>
              <button 
                onClick={() => scrollToSection('industries')}
                className="block w-full text-left text-gray-700 hover:text-[#4d9c71] font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav.industries')}
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="block w-full text-left text-gray-700 hover:text-[#4d9c71] font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav.about')}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-gray-700 hover:text-[#4d9c71] font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav.contact')}
              </button>
              
              {/* Mobile Language Selector */}
              <div className="pt-3 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Language</div>
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                        i18n.language === lang.code ? 'bg-[#4d9c71] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{lang.flag}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button 
                onClick={scrollToContact}
                className="w-full px-6 py-3 bg-[#4d9c71] text-white font-medium rounded-xl cursor-pointer whitespace-nowrap"
              >
                {t('nav.getStarted')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 min-h-screen flex items-center">
        <div className="w-full lg:w-2/3">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full border border-[#4d9c71] bg-transparent mb-8">
            <span className="text-[#4d9c71] text-sm font-medium whitespace-nowrap">{t('hero.badge')}</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t('hero.title')}
          </h1>

          {/* Subheading */}
          <p className="text-xl text-[#B8C5D6] mb-10 leading-relaxed max-w-2xl">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => scrollToSection('services')}
              className="px-8 py-4 bg-[#4d9c71] text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-[#4d9c71]/50 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              {t('hero.exploreSolutions')}
              <i className="ri-arrow-right-line"></i>
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-black transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              {t('hero.learnMore')}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Logo on Right Side */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-auto">
          <img 
            src="https://static.readdy.ai/image/c07ed6529feea951012003251bff6ba4/e8715a4ae386aa03dc56df7a2ca2fc0f.png" 
            alt="GeoSys Logo Background" 
            className="w-full h-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
}
